"use client";

import { motion } from "framer-motion";
import { useEffect, type ReactNode } from "react";

interface HeroLogoProps {
    children?: ReactNode;
    letters?: string[]; // dynamic letters array
}

export default function HeroLogo({ children, letters = [] }: HeroLogoProps) {
    useEffect(() => {
        if (!letters.length) return;

        let totalDelay = 0;

        letters.forEach((selector) => {
            const el = document.querySelector<SVGPathElement>(selector);
            if (!el) return;

            const length = el.getTotalLength();
            el.style.strokeDasharray = String(length);
            el.style.strokeDashoffset = String(length);
            el.style.stroke = "#ccc";
            el.style.fill = "transparent";
            el.style.strokeWidth = "2";
            el.style.transition = "none";

            const drawDuration = 0.4;

            setTimeout(() => {
                // ---- 1) Draw stroke ----
                el.style.transition = `stroke-dashoffset ${drawDuration}s cubic-bezier(0.65, 0, 0.35, 1)`;
                el.style.strokeDashoffset = "0";

                setTimeout(() => {
                    // ---- 2) Fill ----
                    el.style.transition = "fill 0.6s ease-in-out, stroke 0.3s ease-out";
                    el.style.fill = "#DDDDDD";

                    // ---- 3) Keep stroke animating continuously ----
                    setInterval(() => {
                        el.style.transition = `stroke-dashoffset 1.2s linear`;
                        el.style.stroke = "#ccc"; // show stroke again for animation
                        el.style.strokeDashoffset = "0";

                        setTimeout(() => {
                            el.style.transition = `stroke-dashoffset 1.2s linear`;
                            el.style.strokeDashoffset = String(length);
                        }, 1200); // reverse after forward completes
                    }, 2400); // repeat every 2.4s
                }, drawDuration * 800);
            }, totalDelay);

            totalDelay += drawDuration * 1000 - 200;
        });


        const subtitle = document.querySelector<SVGPathElement | SVGTextElement>("#logo-subtitle");
        if (subtitle) {
            const length = (subtitle as SVGPathElement).getTotalLength?.() || 200;
            subtitle.style.strokeDasharray = String(length);
            subtitle.style.strokeDashoffset = String(length);
            subtitle.style.stroke = "#ccc";
            subtitle.style.fill = "transparent";
            subtitle.style.strokeWidth = "2";
            subtitle.style.transition = "none";
            subtitle.style.display = "block";

            setTimeout(() => {
                const drawDuration = 1.2;
                subtitle.style.transition = `stroke-dashoffset ${drawDuration}s cubic-bezier(0.65, 0, 0.35, 1)`;
                subtitle.style.strokeDashoffset = "0";

                setTimeout(() => {
                    subtitle.style.transition = "fill 0.8s ease-in-out, stroke 0.3s ease-out";
                    subtitle.style.fill = "#272D57";
                    subtitle.style.stroke = "transparent";
                }, drawDuration * 400);
            }, totalDelay + 600);
        }

        const bars = document.querySelectorAll<HTMLElement>(".show-bars");
        if (!bars.length) return;

        bars.forEach((bar, index) => {
            bar.style.opacity = "0";
            bar.style.transform = "scaleY(0)";
            bar.style.transformOrigin = "bottom";
            bar.style.transition = "none";

            setTimeout(() => {
                bar.style.transition = "opacity 0.6s ease-out, transform 0.8s ease-out";
                bar.style.opacity = "1";
                bar.style.transform = "scaleY(1)";
            }, totalDelay + 800 + index * 150);
        });
    }, [letters]);

    return (
        <div className="relative w-full border-b border-gray-200 h-[240px] sm:h-[320px] md:h-[550px] lg:h-[650px] 2xl:h-[80vh] section-background-filler flex justify-center items-center">
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="100%"
                height="100%"
                viewBox="0 0 1920 745"
                preserveAspectRatio="xMidYMid meet"
                fill="none"
            >
                <g id="Frame 1618873444">
                    <motion.path
                        id="LeftDiagnolLine"
                        d="M-194.521 1715.53L1319.19 -1243.68"
                        stroke="#ccc"
                        strokeOpacity="0.6"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 1.3, ease: [0.42, 0, 0.58, 1] }}
                    />



                    {/* Right Diagonal Polygon */}
                    <motion.g
                        id="RigthDiagnol"
                        filter="url(#filter0_d_410_753)"
                        initial={{ y: -80, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{
                            type: "spring",
                            stiffness: 50,
                            damping: 25,
                            mass: 0.8,
                            duration: 1.2,
                        }}
                        style={{ filter: "drop-shadow(4px 6px 10px rgba(0,0,0,0.15))" }}
                    >
                        <path d="M1543 445.384H1576L1491 618.384H1458L1543 445.384Z" fill="white" />
                    </motion.g>

                    {/* Left Diagonals */}
                    <motion.g
                        id="LeftDiagnols"
                        initial={{ y: -60, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{
                            type: "spring",
                            stiffness: 50,
                            damping: 25,
                            mass: 0.8,
                            duration: 1.2,
                            staggerChildren: 0.15,
                        }}
                        style={{ filter: "drop-shadow(4px 6px 10px rgba(0,0,0,0.15))" }}
                    >
                        <path
                            id="Rectangle9Path"
                            d="M456 445.384H489L402 618.384H369L456 445.384Z"
                            fill="white"
                        />
                        <path
                            id="Rectangle10Path"
                            d="M515.5 444.884H548.5L461.5 617.884H428.5L515.5 444.884Z"
                            fill="white"
                        />
                    </motion.g>

                    {/* Right Diagonal Line */}
                    <motion.path
                        id="RightDiagnolLine"
                        d="M740.56 2086.84L2255.46 -871.768"
                        stroke="#ccc"
                        strokeOpacity=".6"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 1.3, ease: [0.42, 0, 0.58, 1] }}
                    />
                    {children}
                </g>

                <defs>

                    <filter id="filter0_d_410_753" x="1431" y="399.384" width="220" height="275" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">

                        <feFlood floodOpacity="0" result="BackgroundImageFix" />

                        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />

                        <feOffset dx="24" dy="5" />

                        <feGaussianBlur stdDeviation="25.5" />

                        <feComposite in2="hardAlpha" operator="out" />

                        <feColorMatrix type="matrix" values="0 0 0 0 0.416721 0 0 0 0 0.501976 0 0 0 0 0.581731 0 0 0 0.41 0" />

                        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_410_753" />

                        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_410_753" result="shape" />

                    </filter>

                    <filter id="filter1_d_410_753" x="342" y="399.384" width="222" height="275" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">

                        <feFlood floodOpacity="0" result="BackgroundImageFix" />

                        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />

                        <feOffset dx="24" dy="5" />

                        <feGaussianBlur stdDeviation="25.5" />

                        <feComposite in2="hardAlpha" operator="out" />

                        <feColorMatrix type="matrix" values="0 0 0 0 0.416721 0 0 0 0 0.501976 0 0 0 0 0.581731 0 0 0 0.41 0" />

                        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_410_753" />

                        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_410_753" result="shape" />

                    </filter>

                    <filter id="filter2_d_410_753" x="401.5" y="398.884" width="222" height="275" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">

                        <feFlood floodOpacity="0" result="BackgroundImageFix" />

                        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />

                        <feOffset dx="24" dy="5" />

                        <feGaussianBlur stdDeviation="25.5" />

                        <feComposite in2="hardAlpha" operator="out" />

                        <feColorMatrix type="matrix" values="0 0 0 0 0.416721 0 0 0 0 0.501976 0 0 0 0 0.581731 0 0 0 0.41 0" />

                        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_410_753" />

                        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_410_753" result="shape" />

                    </filter>

                    <filter id="filter3_d_410_753" x="955.99" y="313.664" width="37" height="123" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">

                        <feFlood floodOpacity="0" result="BackgroundImageFix" />

                        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />

                        <feOffset dx="4" dy="9" />

                        <feGaussianBlur stdDeviation="3.5" />

                        <feComposite in2="hardAlpha" operator="out" />

                        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />

                        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_410_753" />

                        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_410_753" result="shape" />

                    </filter>

                    <filter id="filter4_d_410_753" x="1230.99" y="280.664" width="36" height="123" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">

                        <feFlood floodOpacity="0" result="BackgroundImageFix" />

                        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />

                        <feOffset dx="4" dy="9" />

                        <feGaussianBlur stdDeviation="3.5" />

                        <feComposite in2="hardAlpha" operator="out" />

                        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />

                        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_410_753" />

                        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_410_753" result="shape" />

                    </filter>

                    <filter id="filter5_d_410_753" x="604.99" y="267.664" width="38" height="174" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">

                        <feFlood floodOpacity="0" result="BackgroundImageFix" />

                        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />

                        <feOffset dx="4" dy="9" />

                        <feGaussianBlur stdDeviation="3.5" />

                        <feComposite in2="hardAlpha" operator="out" />

                        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />

                        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_410_753" />

                        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_410_753" result="shape" />

                    </filter>

                    <clipPath id="clip0_410_753">

                        <rect width="909" height="358" fill="white" transform="translate(548.5 260.384)" />

                    </clipPath>

                    <clipPath id="clip1_410_753">

                        <rect width="909" height="162" fill="white" transform="translate(548.5 260.384)" />

                    </clipPath>

                </defs>

            </svg>
        </div>
    );
}
